import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import MapToolbar from './MapToolbar.jsx';
import MapActions from './MapActions.jsx';
import MapChecklist from './MapChecklist.jsx';
import InlineStatus from './InlineStatus.jsx';
import { MapCaptureRegion, useMapStatsConfig, captureRegionAsBlob } from './MapStats.jsx';
import MapAutoFit from './MapAutoFit.jsx';
import { useTravelSave } from './travel/TravelSaveContext';
import { makeThumbnail } from '../utils/mapThumbnail';
import { storage } from '../firebaseApp';
import { orderProduct } from '../actions/shapeActions';
import {
    trackLocationToggle,
    trackDownload,
    trackMapReset,
    trackVisitedColorChange,
    trackAddToCart,
} from '../utils/analytics';
import { trackFbDownload, trackFbAddToCart } from '../utils/facebookPixel';
import { trackPinDownload, trackPinAddToCart } from '../utils/pinterestTag';
import { PALETTE, alpha } from '../theme';

// Brand coral — matches the warm palette in src/theme.js. Users can still
// pick any colour from the picker; this is just the first-paint default.
const DEFAULT_VISITED_COLOR = { r: 232, g: 99, b: 45, a: 255 };

const rgbToHex = (r, g, b) =>
    '#' + [r, g, b].map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('');

/**
 * Layout / state / behavior shared by every Travel Map section. Per-map
 * data (items, title, sub) and the per-map renderer are passed in.
 *
 *   ┌─ header (title + subtitle) ─────────────────────────────┐
 *   │ inline status banner (only when there's a message)      │
 *   │                                                         │
 *   │   ┌── STATS HERO ─────────────────────────────────────┐ │
 *   │   │  X / Y  unitLabel visited       45% complete      │ │
 *   │   │  ─────────────━━━━━━━━━━━━━━━━━━━━━━━━━━━━━──── │ │
 *   │   └───────────────────────────────────────────────────┘ │
 *   │                                                         │
 *   │   [ map options toolbar — collapsed accordion        ]  │
 *   │   [ legend (optional)                                ]  │
 *   │   [ MAP                                              ]  │
 *   │   [ checklist accordion                              ]  │
 *   │   [ product gallery                                  ]  │
 *   └─────────────────────────────────────────────────────────┘
 */
const TravelMapShell = ({
    mapType,
    analyticsLocationType,
    trackingLabel,
    storageKey,
    title,
    subtitle,
    items,
    noun,
    unitLabel,
    // Region-qualified noun used in the default Zazzle caption only —
    // e.g. "European countries" so the printed caption reads "5 European
    // countries visited" rather than the generic "5 countries visited".
    // Stats UI and other surfaces continue to use `unitLabel` (which
    // omits the region) so headings and counters stay compact.
    captionUnit,
    share,
    defaultColor = DEFAULT_VISITED_COLOR,
    extraOptions = null,
    showLegend = false,
    renderMap
}) => {
    const dispatch = useDispatch();
    const mapRef = useRef(null);
    // Inner ref points at the map-only div (no stats badge) — used for live
    // product previews so the USA shape is centered in the captured PNG.
    // Downloads + orders still capture the full mapRef (stats included).
    const mapInnerRef = useRef(null);
    const [visited, setVisited] = useState([]);
    const [visitedColor, setVisitedColor] = useState(defaultColor);
    const [statsConfig, setStatsConfig] = useMapStatsConfig({ mapType, label: unitLabel });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ message: '', variant: 'info' });

    // Region + reopen bridge (see TravelSaveContext). Declared up here so Reset
    // can clear the open-saved-map link.
    const travelSave = useTravelSave();

    const hexColor = useMemo(
        () => rgbToHex(visitedColor.r, visitedColor.g, visitedColor.b),
        [visitedColor]
    );

    const toggle = useCallback((id) => {
        const wasVisited = visited.includes(id);
        const item = items.find((i) => i.id === id);
        trackLocationToggle(mapType, analyticsLocationType, item?.name ?? id, !wasVisited);
        setVisited((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, [visited, items, mapType, analyticsLocationType]);

    const handleReset = useCallback(() => {
        trackMapReset(mapType, visited.length);
        setVisited([]);
        // Reset = start a fresh map: drop the open-saved-map link so the next
        // Save creates a new map instead of offering to overwrite.
        if (travelSave) travelSave.setOpenMap(null);
    }, [mapType, visited.length, travelSave]);

    const handleSelectAll = useCallback(() => {
        setVisited(items.map((i) => i.id));
    }, [items]);

    const handleDownload = useCallback(async () => {
        try {
            setLoading(true);
            trackDownload({
                kind: 'travel', mapType,
                visitedCount: visited.length, totalCount: items.length
            });
            trackFbDownload(trackingLabel);
            trackPinDownload(trackingLabel);
            const blob = await captureRegionAsBlob(mapRef.current);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = share.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setStatus({ message: 'Map downloaded.', variant: 'success' });
        } catch (error) {
            console.error('Error downloading map:', error);
            setStatus({ message: 'Could not download the map. Please try again.', variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [mapType, trackingLabel, visited.length, items.length, share.fileName]);

    /**
     * Capture the current map region and upload it to storage, resolving
     * with the public URL. Called by ProductGallery on each Order click.
     */
    const prepareImageUpload = useCallback(
        (onProgress) =>
            new Promise(async (resolve, reject) => {
                try {
                    const blob = await captureRegionAsBlob(mapRef.current);
                    const uniqueId =
                        Math.random().toString(36).substring(2) +
                        Date.now().toString(36) + '.png';
                    const storageRef = ref(storage, 'images/' + uniqueId);
                    const uploadTask = uploadBytesResumable(storageRef, blob);
                    uploadTask.on(
                        'state_changed',
                        (snapshot) => {
                            if (onProgress) {
                                onProgress(Math.round(
                                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                                ));
                            }
                        },
                        (err) => reject(err),
                        async () => {
                            try {
                                const url = await getDownloadURL(uploadTask.snapshot.ref);
                                resolve(url);
                            } catch (err) {
                                reject(err);
                            }
                        }
                    );
                } catch (err) {
                    reject(err);
                }
            }),
        []
    );

    const handleProductOrder = useCallback(
        ({ product, label, imageUrl }) => {
            trackAddToCart({
                product: product.analyticsKey, kind: 'travel', mapType,
                visitedCount: visited.length, totalCount: items.length
            });
            trackFbAddToCart(product.name, trackingLabel);
            trackPinAddToCart(product.name, trackingLabel);
            dispatch(orderProduct(imageUrl, product, label));
        },
        [mapType, trackingLabel, visited.length, items.length, dispatch]
    );

    const handleColorChange = useCallback((channel, value) => {
        setVisitedColor((prev) => {
            const next = { ...prev, [channel]: value };
            trackVisitedColorChange(mapType, rgbToHex(next.r, next.g, next.b));
            return next;
        });
    }, [mapType]);

    const handlePresetClick = useCallback((color) => {
        setVisitedColor(color);
    }, []);

    // ── Saved-map adapter (Travel) ─────────────────────────────────────────
    // Region + reopen payload come from Main via context (the region selection
    // lives there); visited / colour / stats live here. See TravelSaveContext.
    // `travelSave` is declared near the top so Reset can use it.
    const reopen = travelSave && travelSave.reopen;
    useEffect(() => {
        if (!reopen || !reopen.settings) return;
        const s = reopen.settings;
        if (Array.isArray(s.visited)) setVisited(s.visited);
        if (s.visitedColor) setVisitedColor(s.visitedColor);
        if (s.statsConfig) setStatsConfig(s.statsConfig);
        travelSave.consumeReopen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reopen]);

    const getSnapshot = useCallback(async () => {
        let thumbnailBlob = null;
        try {
            thumbnailBlob = await makeThumbnail(await captureRegionAsBlob(mapRef.current));
        } catch {
            /* thumbnail is best-effort */
        }
        const regionLabel = (travelSave && travelSave.regionLabel) || 'Travel map';
        return {
            type: 'travel',
            defaultName: regionLabel,
            breadcrumb: ['Travel', regionLabel],
            settings: {
                mapType: travelSave ? travelSave.regionKey : mapType,
                visited,
                visitedColor,
                statsConfig,
            },
            thumbnailBlob,
        };
    }, [travelSave, mapType, visited, visitedColor, statsConfig]);

    const captionLabel = `${visited.length} ${captionUnit || unitLabel} visited`;

    // Live design URL: a debounced PNG capture of the current map so product
    // cards can show the user's actual design instead of a generic placeholder.
    // We only refresh once the user has picked at least one place — there's
    // nothing meaningful to overlay before that.
    const [designUrl, setDesignUrl] = useState(null);
    useEffect(() => {
        if (visited.length === 0) {
            setDesignUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                // Use the full mapRef (stats badge + map) so the preview
                // matches what's actually printed on the product — the
                // downloaded/uploaded PNG also includes the stats badge.
                const blob = await captureRegionAsBlob(mapRef.current);
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                setDesignUrl((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return url;
                });
            } catch (err) {
                if (!cancelled) console.warn('Live preview capture failed:', err);
            }
        }, 350);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [visited, hexColor, statsConfig]);

    // Revoke the object URL on unmount so we don't leak it.
    useEffect(() => () => {
        if (designUrl) URL.revokeObjectURL(designUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {/* ── Header ───────────────────────────────────────────── */}
            <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2.5 } }}>
                <Typography
                    component="h2"
                    sx={{
                        fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                        fontWeight: 800,
                        color: PALETTE.text,
                        letterSpacing: '-0.02em',
                        mb: 0.5,
                        lineHeight: 1.1
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        color: PALETTE.textBody,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        maxWidth: 560,
                        mx: 'auto',
                        lineHeight: 1.5
                    }}
                >
                    {subtitle}
                </Typography>
            </Box>

            {/* ── Inline status (download / error) ─────────────────── */}
            <Box sx={{ mb: status.message ? 1.5 : 0 }}>
                <InlineStatus
                    message={status.message}
                    variant={status.variant}
                    onClose={() => setStatus({ message: '', variant: 'info' })}
                />
            </Box>

            {/* ── Visited count — quiet centred line, no progress chrome. */}
            <Box sx={{ textAlign: 'center', mb: { xs: 1.5, sm: 2 } }}>
                <Typography sx={{
                    fontSize: { xs: '0.95rem', sm: '1.05rem' },
                    color: PALETTE.text,
                    fontWeight: 500
                }}>
                    <Box component="strong" sx={{ fontWeight: 800, color: PALETTE.primary }}>
                        {visited.length}
                    </Box>
                    {' '}/ {items.length} {unitLabel} visited
                </Typography>
            </Box>

            {/* ── Map options (color / stats badge) — above the map ─ */}
            <MapToolbar
                visitedColor={visitedColor}
                onColorChange={handleColorChange}
                onPresetClick={handlePresetClick}
                statsConfig={statsConfig}
                onStatsChange={setStatsConfig}
                accent={hexColor}
                extraOptions={extraOptions}
            />

            {/* ── Download in-flight progress ──────────────────────── */}
            {loading && (
                <Box sx={{ mb: { xs: 1, sm: 1.25 } }}>
                    <LinearProgress />
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: PALETTE.textMuted }}>
                        Preparing download…
                    </Typography>
                </Box>
            )}

            {/* ── Legend (optional, per-map) ───────────────────────── */}
            {showLegend && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: { xs: 1, sm: 1.25 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: hexColor, border: '2px solid #fff', boxShadow: `0 0 0 1px ${alpha('text', 0.10)}` }} />
                        <Typography variant="caption" sx={{ color: PALETTE.text, fontWeight: 600 }}>Visited</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: PALETTE.textVeryFaint, border: '2px solid #fff', boxShadow: `0 0 0 1px ${alpha('text', 0.10)}` }} />
                        <Typography variant="caption" sx={{ color: PALETTE.textMuted }}>Not visited</Typography>
                    </Box>
                </Box>
            )}

            {/* ── Map ──────────────────────────────────────────────── */}
            <Box sx={{ mb: { xs: 2, sm: 2.5 }, display: 'flex', justifyContent: 'center' }}>
                <MapCaptureRegion
                    ref={mapRef}
                    innerRef={mapInnerRef}
                    statsConfig={statsConfig}
                    visited={visited.length}
                    total={items.length}
                    accent={hexColor}
                    sx={{
                        '& svg':  { width: '100%', height: 'auto', display: 'block' },
                        '& path': { cursor: 'pointer', transition: 'all 0.2s ease', outline: 'none' },
                    }}
                >
                    <MapAutoFit>
                        {renderMap({ visited, hexColor, toggle })}
                    </MapAutoFit>
                </MapCaptureRegion>
            </Box>

            {/* ── Actions below the map (Order strip + Reset / Download / Share) ── */}
            <MapActions
                visitedCount={visited.length}
                loading={loading}
                onReset={handleReset}
                onDownload={handleDownload}
                share={{
                    region: share.region,
                    visited: visited.length,
                    total: items.length,
                    label: unitLabel,
                    getBlob: () => captureRegionAsBlob(mapRef.current),
                    fileName: share.fileName,
                    trackId: share.trackId
                }}
                save={travelSave && travelSave.regionKey ? {
                    getSnapshot,
                    openMap: travelSave.openMap,
                    onSaved: travelSave.setOpenMap,
                    onStatus: travelSave.onStatus,
                } : undefined}
                prepareImage={prepareImageUpload}
                onOrder={handleProductOrder}
                defaultLabel={captionLabel}
                designSrc={designUrl}
            />

            {/* ── Checklist + selection summary ────────────────────── */}
            <MapChecklist
                items={items}
                visited={visited}
                onToggle={toggle}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleReset}
                noun={noun}
                hexColor={hexColor}
            />

            {visited.length > 0 && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="caption" sx={{ color: PALETTE.textMuted }}>
                        Selected: {[...visited].sort().join(', ')}
                    </Typography>
                </Box>
            )}

        </Box>
    );
};

TravelMapShell.propTypes = {
    mapType: PropTypes.string.isRequired,
    analyticsLocationType: PropTypes.string.isRequired,
    trackingLabel: PropTypes.string.isRequired,
    storageKey: PropTypes.string.isRequired,
    title: PropTypes.node.isRequired,
    subtitle: PropTypes.node.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            subtitle: PropTypes.string
        })
    ).isRequired,
    noun: PropTypes.string.isRequired,
    unitLabel: PropTypes.string.isRequired,
    captionUnit: PropTypes.string,
    share: PropTypes.shape({
        region: PropTypes.string.isRequired,
        fileName: PropTypes.string.isRequired,
        trackId: PropTypes.string.isRequired
    }).isRequired,
    defaultColor: PropTypes.object,
    extraOptions: PropTypes.node,
    showLegend: PropTypes.bool,
    renderMap: PropTypes.func.isRequired
};

export default TravelMapShell;
